import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export const RegisterModal = ({ program, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const open = !!program;
  const priced = program && program.price > 0;
  const full = program && program.full;

  const close = () => { onClose(); setTimeout(() => { setDone(false); setWaitlisted(false); setForm({ name: "", email: "", phone: "", note: "" }); }, 300); };

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (!/^\d{7,15}$/.test(form.phone.replace(/\s/g, ""))) return toast.error("Please enter a valid phone number");
    setLoading(true);
    try {
      const res = await api.post("/registrations", { program_id: program.id, ...form, phone: form.phone.replace(/\s/g, "") });
      if (res.data.waitlisted) { setWaitlisted(true); setDone(true); toast.success("Added to the waitlist"); return; }
      if (res.data.free) { setDone(true); toast.success("You're registered!"); return; }
      const { registration, order_id, key_id, amount } = res.data;
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment gateway."); return; }
      setLoading(false);
      const rzp = new window.Razorpay({
        key: key_id, amount, currency: "INR", order_id,
        name: "Serene Mind Place", description: program.title,
        prefill: { name: form.name, email: form.email, contact: form.phone.replace(/\s/g, "") },
        theme: { color: "#D98BA0" },
        handler: async (resp) => {
          setLoading(true);
          try { await api.post("/registrations/verify", { registration_id: registration.id, ...resp }); setDone(true); toast.success("Payment received — you're registered!"); }
          catch { toast.error("Payment verification failed — please contact us on 083176 34326."); }
          finally { setLoading(false); }
        },
        modal: { ondismiss: () => toast("Payment cancelled — your spot isn't held yet.") },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4 bg-[#20301A]/50 backdrop-blur-sm" onClick={close}>
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
            data-testid="register-modal"
            className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-[28px] bg-[#F2F8E8] soft-shadow p-6 sm:p-8 relative">
            <button onClick={close} data-testid="register-close" className="absolute top-5 right-5 text-[#55703F] hover:text-[#20301A]"><X size={20} /></button>

            {done ? (
              <div className="py-10 text-center" data-testid="register-success">
                <div className={`mx-auto grid place-items-center w-16 h-16 rounded-full text-white ${waitlisted ? "bg-[#C2748C]" : "bg-[#D98BA0]"}`}><Check size={30} /></div>
                <h3 className="font-serif text-3xl mt-5 text-[#20301A]">{waitlisted ? "You're on the waitlist" : "You're registered"}</h3>
                <p className="text-[#55703F] mt-3 max-w-sm mx-auto">
                  {waitlisted
                    ? <>Thanks, {form.name.split(" ")[0]}. This one's full, so we've added you to the waitlist and emailed <b>{form.email}</b> — we'll reach out the moment a spot opens. No payment taken.</>
                    : <>Thank you, {form.name.split(" ")[0]}. We've emailed a confirmation to <b>{form.email}</b> and will reach out with next steps.</>}
                </p>
                <button onClick={close} className="mt-7 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-7 py-3 text-sm hover:bg-[#20301A] transition-colors">Done</button>
              </div>
            ) : (
              <>
                <span className="overline text-[#C77D94]">{program.kind === "workshop" ? "Register — workshop" : "Claim this offer"}</span>
                <h3 className="font-serif text-2xl sm:text-3xl mt-2 text-[#20301A]">{program.title}</h3>
                {program.date && <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#55703F]"><CalendarDays size={14} /> {program.date}</p>}
                {program.description && <p className="mt-3 text-sm text-[#3C5A2E] leading-relaxed">{program.description}</p>}
                {priced && <p className="mt-3 font-serif text-2xl text-[#D98BA0]">₹{program.price / 100}</p>}

                <div className="mt-5 space-y-3">
                  <input data-testid="register-name-input" placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0]" />
                  <input data-testid="register-email-input" type="email" placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0]" />
                  <input data-testid="register-phone-input" placeholder="Phone number" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0]" />
                  <textarea data-testid="register-note-input" rows={2} placeholder="Anything you'd like us to know? (optional)" value={form.note} onChange={(e) => set("note", e.target.value)}
                    className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#D98BA0] resize-none" />
                </div>

                <button data-testid="register-submit-button" onClick={submit} disabled={loading}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D98BA0] text-white px-6 py-3.5 text-sm hover:bg-[#c2748c] transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Processing…</> : full ? <>Join the waitlist <Check size={16} /></> : priced ? <>Pay ₹{program.price / 100} & Register <Check size={16} /></> : <>Register now <Check size={16} /></>}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
